(() => {
  const url = (path, variantId) => {
    const target = new URL(path, window.location.origin);
    if (target.origin !== window.location.origin) throw new Error('Invalid product origin.');
    const params = new URLSearchParams(window.location.search);
    target.searchParams.forEach((value, key) => params.set(key, value));
    params.delete('option_values');
    params.set('variant', String(variantId));
    return `${target.pathname}?${params}${target.hash || window.location.hash}`;
  };

  const guard = (form, variantInput) => {
    const button = form.querySelector('button[name="add"]');
    let busy = false;
    let canBuy = Boolean(variantInput?.value) && !button?.disabled;
    let paymentReady = Boolean(form.querySelector('.shopify-payment-button'));
    let paymentProductId = form.querySelector('input[name="product-id"]')?.value || '';
    let paymentRequest = 0;
    const refresh = () => {
      form.setAttribute('aria-busy', String(busy));
      if (button) button.disabled = busy || !canBuy;
      const selectedProductId = form.querySelector('input[name="product-id"]')?.value || '';
      form.querySelectorAll('.shopify-payment-button').forEach((node) => {
        node.hidden = !paymentReady || busy || !canBuy || !paymentProductId || paymentProductId !== selectedProductId;
      });
    };
    form.addEventListener('submit', (event) => {
      if (!busy && canBuy && variantInput?.value) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    return {
      busy(value) { busy = value; refresh(); },
      block() { canBuy = false; if (variantInput) variantInput.value = ''; refresh(); },
      commit(variant, product) {
        if (!variantInput || !variant?.id || !Number.isFinite(Number(variant.price))) return false;
        paymentRequest += 1;
        variantInput.value = String(variant.id);
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (product?.id) form.querySelectorAll('input[name="product-id"]').forEach((input) => { input.value = String(product.id); });
        canBuy = Boolean(variant.available);
        if (button) button.textContent = canBuy ? button.dataset.addLabel : button.dataset.unavailableLabel;
        refresh();
        return true;
      },
      paymentReady(value) { paymentReady = Boolean(value); refresh(); },
      paymentProductId(value) { paymentProductId = String(value || ''); refresh(); },
      async refreshPaymentButton(productUrl, product, variant) {
        const request = ++paymentRequest;
        paymentReady = false;
        refresh();
        const selectedProductId = form.querySelector('input[name="product-id"]')?.value || '';
        if (!productUrl || !product?.id || String(product.id) !== selectedProductId || String(variantInput?.value) !== String(variant?.id)) return false;
        try {
          const target = new URL(productUrl, window.location.origin);
          if (target.origin !== window.location.origin) throw new Error('Invalid product URL');
          const sectionId = form.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || 'main';
          target.searchParams.set('sections', sectionId);
          target.searchParams.set('variant', String(variant.id));
          const response = await fetch(target.href, { headers: { Accept: '*/*' }, signal: AbortSignal.timeout(12000) });
          if (!response.ok) throw new Error('Payment button data unavailable');
          const sections = await response.json();
          if (request !== paymentRequest) return false;
          const markup = sections?.[sectionId];
          if (typeof markup !== 'string') throw new Error('Product section missing');
          const section = document.createElement('template');
          section.innerHTML = markup;
          const sectionProductId = section.content.querySelector('input[name="product-id"]')?.value;
          const sectionVariantIds = [...section.content.querySelectorAll('input[name="id"]')].map((input) => input.value);
          if (String(sectionProductId) !== String(product.id) || !sectionVariantIds.includes(String(variant.id))) {
            throw new Error('Rendered checkout product or variant did not match the selection');
          }
          if (request !== paymentRequest || form.querySelector('input[name="product-id"]')?.value !== String(product.id) || String(variantInput.value) !== String(variant.id)) return false;
          let productContext = null;
          try { productContext = JSON.parse(section.content.querySelector('[data-rdr-product-context]')?.textContent || 'null'); } catch {}
          document.dispatchEvent(new CustomEvent('rdr21:product-context', { detail: { product, context: productContext } }));
          const nextButton = section.content.querySelector('.shopify-payment-button');
          if (!nextButton) throw new Error('Payment button missing');
          nextButton.querySelectorAll('script, iframe, object, embed, form').forEach((node) => node.remove());
          nextButton.querySelectorAll('*').forEach((node) => [...node.attributes].forEach((attribute) => {
            if (/^on/i.test(attribute.name) || (attribute.name === 'href' || attribute.name === 'src') && /^\s*javascript:/i.test(attribute.value)) node.removeAttribute(attribute.name);
          }));
          if (request !== paymentRequest || form.querySelector('input[name="product-id"]')?.value !== String(product.id) || String(variantInput.value) !== String(variant.id)) return false;
          const activeButton = form.querySelector('.shopify-payment-button');
          if (!activeButton) return false;
          activeButton.replaceWith(nextButton);
          paymentProductId = String(product.id);
          paymentReady = true;
          refresh();
          return true;
        } catch (error) {
          console.warn('RDR product section refresh failed:', error.message);
          if (request === paymentRequest) {
            paymentReady = false;
            refresh();
          }
          return false;
        }
      },
    };
  };

  window.Rdr21ProductSelection = { url, guard };

  document.querySelectorAll('select[data-product-variants]').forEach((select) => {
    const form = select.closest('form');
    if (!form) return;
    const state = guard(form, select);
    select.addEventListener('change', () => {
      const variants = JSON.parse(select.dataset.productVariants || '[]');
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!state.commit(variant)) { state.block(); return; }
      const price = document.querySelector('[data-autodesk-price]');
      const currency = price?.querySelector('small')?.textContent.trim() || 'EUR';
      const money = (value) => new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(Number(value) / 100);
      const current = price?.querySelector('[data-price-current]');
      const compare = price?.querySelector('[data-price-compare]');
      const saleDeadline = document.querySelector('[data-sale-deadline]');
      const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
      if (current) current.textContent = money(variant.price);
      if (compare) {
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? money(variant.compare_at_price) : '';
      }
      if (saleDeadline) saleDeadline.hidden = !hasCompare;
      window.history.replaceState(window.history.state, '', url(window.location.pathname, variant.id));
    });
  });
})();
